import type { SubmitHandler } from "react-hook-form";
import { Controller, useForm } from "react-hook-form";
import { useToken } from "utils/context";
import KaboodleModal from "./KaboodleModal";
import Card from "components/Card/Card";
import { useCallback, useMemo } from "react";
import { useSelector } from "utils/redux";
import { useLazyGetImportTemplateQuery } from "services/api";
import Button from "components/Button/Button";
import type { OptionType } from "components/inputs";
import { Select, Checkbox } from "components/inputs";
import { useConstants } from "utils/hooks";

type DownloadClaimAndQueryModalPropTypes = {
  isOpen: boolean;
  onClose: () => void;
};

type FormData = {
  importType: OptionType;
  includeClaims: boolean;
};

const DownloadClaimAndQueryModal = ({
  isOpen,
  onClose,
}: DownloadClaimAndQueryModalPropTypes) => {
  const { accessToken } = useToken();

  const {
    control,
    reset,
    handleSubmit,
    clearErrors,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      importType: { label: "Claim", value: "C" },
      includeClaims: false,
    },
  });

  const { constants } = useConstants();
  // only include claim and query import types
  const importTypes = (constants?.IMPORT_TYPES || []).filter(
    (importType) =>
      !!importType.value && ["C", "Q"].includes(importType.value.toString()),
  );
  const currentRRE = useSelector((state) => state.rre.currentRRE);

  const [triggerGetImportTemplate, { isError, isLoading }] =
    useLazyGetImportTemplateQuery();

  const footerContent = useMemo(() => {
    const onSubmit: SubmitHandler<FormData> = async (data: FormData) => {
      const url = data.importType.value === "C" ? "claim" : "query";
      const fileName = `${data.importType.value === "C" ? "claim" : "query"}_import_template.csv`;
      const rreId = data.includeClaims ? currentRRE?.id : null;

      await triggerGetImportTemplate({
        fileName,
        rreId,
        url,
      })
        .unwrap()
        .then(() => {
          reset();
          onClose();
        });
    };

    return (
      <div>
        <div className="upload-button">
          <Button onClick={handleSubmit(onSubmit)} disabled={isLoading}>
            {isError ? "Try Again" : isLoading ? "Downloading..." : "Download"}
          </Button>
        </div>
      </div>
    );
  }, [
    accessToken,
    handleSubmit,
    isError,
    isLoading,
    onClose,
    reset,
    triggerGetImportTemplate,
  ]);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  return (
    <KaboodleModal isOpen={isOpen} onClose={handleClose} footer={footerContent}>
      <Card heading="Download Templates">
        {isError && (
          <div className="flex flex-col">
            <div className="error-footer">
              <p>{"An error occurred, please try again"}</p>
            </div>
          </div>
        )}
        <Controller
          control={control}
          name="importType"
          rules={{ required: "Import type is required" }}
          render={({ field: { name, onChange } }) => (
            <Select
              key={name}
              name={name}
              placeholder="- Select Export Type -"
              label="Import Type"
              options={importTypes}
              onChange={(option) => {
                onChange(option);
                clearErrors();
              }}
              errors={errors}
            />
          )}
        />
        <Controller
          control={control}
          name="includeClaims"
          render={({ field: { name, onChange } }) => (
            <Checkbox
              key={name}
              name={name}
              label="Export Claims?"
              required={false}
              onChange={onChange}
            />
          )}
        />
      </Card>
    </KaboodleModal>
  );
};

export default DownloadClaimAndQueryModal;
